import { Listbox, Dialog, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import {
  Fragment,
  ChangeEvent,
  ReactElement,
  useState,
  useRef,
} from "react";
import "./App.css";
import {
  Master,
  checkQuiz,
  init,
  loadUserAudio,
  pauseSound,
  playSound,
  setQuiz,
  updateFilter,
  updateMasterVolume,
} from "./master";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import RefreshIcon from "@mui/icons-material/Refresh";

// import "@fontsource/public-sans";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { Slider } from "@mui/joy";

import React from "react";
import {
  boostCutMarks,
  boostMarks,
  cutMarks,
  exponentialToLinear,
  frequencyMarks,
  linearToExponential,
  linearToExponentialScale,
  frequencyMax,
  frequencyMin,
  exponentialToLinearFloat
} from "./frequencies";
import { scaleFloat } from "./helpers";
import { useRect } from "./useRect";


function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}
const levels = ["Boost", "Cut", "Boost/Cut"];
const FilterSelect: React.FC<{ master: Master; setLevel: Function }> = ({ master, setLevel }) => {
  const [selected, setSelected] = useState(levels[master.level - 1]);

  return (
    <Listbox
      value={selected}
      onChange={(newValue) => {
        master.level = (newValue + 1) as unknown as number;
        setLevel(master.level);
        setSelected(levels[master.level - 1]);
        if (master.playing) {
          master.selectedBuffer?.stop(master.atx?.currentTime);
          playSound();
        }
      }}
    >
      {({ open }) => (
        <>
          <Listbox.Label className="block text-sm font-medium leading-6 text-gray-400">
            Filter Type
          </Listbox.Label>
          <div className="relative mt-2">
            <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <span className="block truncate">{selected}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {levels.map((thisValue, index) => (
                  <Listbox.Option
                    key={index}
                    className={({ active }) =>
                      classNames(
                        active ? "bg-indigo-600 text-white" : "text-gray-900",
                        "relative cursor-default select-none py-2 pl-8 pr-4"
                      )
                    }
                    value={index}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "block truncate"
                          )}
                        >
                          {thisValue}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 left-0 flex items-center pl-1.5"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};
const SampleSelect: React.FC<{ master: Master; setSelectedSample: Function; setSelectedSampleName: Function; selectedSampleName: string; filename: string }> = ({
  master,
  setSelectedSample,
  setSelectedSampleName,
  selectedSampleName,
  filename,
}) => {
  const samples = [
    "White Noise",
    "Pink Noise",
    "Funky Blues",
    "Aquasordido",
    "Montuno",
    "Soul Shuffle 2",
    "Wade in the Water",
    "Drum Kit",
    "Piano",
    "Guitar",
    "Lead Vocal",
    "Backing Vocals",
    filename,
  ];
  // const [selected, setSelected] = useState(samples[master.selectedSample]);
  return (
    <Listbox
      defaultValue={selectedSampleName}
      value={selectedSampleName}
      onChange={(newValue) => {
        if (newValue !== null) {
          master.selectedSample = newValue as unknown as number;
          setSelectedSample(master.selectedSample);
          setSelectedSampleName(samples[master.selectedSample]);
          if (master.playing) {
            master.selectedBuffer?.stop(master.atx?.currentTime);
            playSound();
          }
        }
      }}
    >
      {({ open }) => (
        <>
          <Listbox.Label className="block text-sm font-medium leading-6 text-gray-400">
            Audio Source
          </Listbox.Label>
          <div className="relative mt-2">
            <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <span className="block truncate">{selectedSampleName}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {samples.map((thisValue, index) => (
                  <Listbox.Option
                    key={index}
                    className={({ active }) =>
                      classNames(
                        active ? "bg-indigo-600 text-white" : "text-gray-900",
                        "relative cursor-default select-none py-2 pl-8 pr-4"
                      )
                    }
                    value={index}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "block truncate"
                          )}
                        >
                          {thisValue}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 left-0 flex items-center pl-1.5"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};
function easeInRange(i: number, max: number, power: number) {
  let scaled = scaleFloat(i, 0, max, 0, 1);
  let eased = easeInQuint(scaled, power);
  let normalized = scaleFloat(eased, 0, 1, 0, max);
  return normalized;
}
function easeInQuint(x: number, power: number): number {
  return Math.pow(x, power);
}
function easeOutRange(i: number, minCrop: number, maxRange: number, maxWidth: number) {
  let eased = exponentialToLinearFloat(i, [minCrop, maxRange]);
  let normalized = scaleFloat(eased, 0, maxRange, 0, maxWidth);
  return normalized;
}


let animationController;
export const Controls: React.FC<{ master: Master }> = ({ master }) => {
  const [filename, setFilename] = useState("User Upload");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyser = useRef<AnalyserNode>();
  const samples = [
    "White Noise",
    "Pink Noise",
    "Funky Blues",
    "Aquasordido",
    "Montuno",
    "Soul Shuffle 2",
    "Wade in the Water",
    "Drum Kit",
    "Piano",
    "Guitar",
    "Lead Vocal",
    "Backing Vocals",
    filename,
  ];
  const [playing, setPlaying] = useState(master.playing);
  const [selectedSample, setSelectedSample] = useState(master.selectedSample);
  const [selectedSampleName, setSelectedSampleName] = useState(samples[master.selectedSample]);
  const [level, setLevel] = useState(master.level);
  const [filterState, setFilterState] = useState("A");
  const [quizState, setQuizState] = useState(false);
  const [frequency, setFrequency] = useState(master.filterFrequency);
  const [gain, setGain] = useState(master.filterGain);
  const [masterVolume, setMasterVolume] = useState(master.masterVolumeAmount);
  const [playerRect, playerRef] = useRect("resize");
  const [filterARect, filterARef] = useRect("resize");
  const [filterBRect, filterBRef] = useRect("resize");
  const [visRect, visRef] = useRect("resize");
  const [initialized, setInitialized] = useState(master.initialized);


  const visualizeData = () => {
    animationController = window.requestAnimationFrame(visualizeData);
    analyser.current = master.analyser;
    if (playing) {
      cancelAnimationFrame(animationController);
    }
    if (analyser.current && canvasRef.current) {
      // highest frequencyBinCount goes up to half the sample rate e.g. 20khz
      // dividing by 2 brings it down to 10khz
      const songData = new Uint8Array(analyser.current.frequencyBinCount / 2);

      analyser.current.getByteFrequencyData(songData);
      const ctx = canvasRef.current.getContext("2d");

      if (ctx) {

        if (window.innerWidth < 702 + 48) {
          canvasRef.current.width = window.innerWidth - 66;
        } else {
          canvasRef.current.width = 702
        }

        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        let nextXPosition = 0;
        let minCrop = 2;
        for (let i = minCrop; i < songData.length; i++) {
          let xPosition = easeOutRange(i, minCrop, songData.length, canvasRef.current.width)
          if (i !== songData.length) {
            nextXPosition = easeOutRange(i + 1, minCrop, songData.length, canvasRef.current.width)
          } else {
            nextXPosition = easeOutRange(i, minCrop, songData.length, canvasRef.current.width)
          }

          let thisBarWidth = nextXPosition - xPosition;
          //create a gradient for the  whole canvas
          let gradient = ctx.createLinearGradient(
            0,
            0,
            canvasRef.current.width,
            canvasRef.current.height
          );
          gradient.addColorStop(0.0, "#312E81");
          gradient.addColorStop(1.0, "#6366F1");
          ctx.fillStyle = gradient;
          let maxBarHeight = 255
          let heightRatio = (maxBarHeight) / canvasRef.current.height
          let scaledHeight = easeInRange(songData[i], maxBarHeight, 3)
          ctx.fillRect(xPosition, canvasRef.current.height, thisBarWidth, (-scaledHeight / heightRatio));
        }
      }
    }
  };


  const playIconSize = 60;
  let playButton: ReactElement = (
    <button
      type="button"
      className="rounded-full bg-indigo-600 text-indigo-50 shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
    >
      {playing ? (
        <PauseCircleIcon
          sx={{
            width: `${playIconSize}px`,
            height: `${playIconSize}px`,
          }}
          onClick={() => {
            init();
            pauseSound();
            setPlaying(master.playing);
            visualizeData();
          }}
        />
      ) : (
        <PlayCircleIcon
          sx={{
            width: `${playIconSize}px`,
            height: `${playIconSize}px`,
          }}
          onClick={() => {
            init();
            playSound();
            setPlaying(master.playing);
            visualizeData();
          }}
        />
      )}
    </button>
    // <IconButton variant="plain" size="lg">
    // </IconButton>
  );
  let initializeWindow: ReactElement = (
    <>
      <div className="fixed inset-0 z-40 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          {/* <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all my-8 w-full max-w-sm p-6"> */}
          {/* <div>
              <div className="text-center mt-5">
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  Load Audio
                </h3>
              </div>
            </div> */}

          <button
            type="button"
            className="inline-flex  max-w-sm shadow-xl w-full p-8 justify-center rounded-md bg-indigo-600 text-lg font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => {
              init()
              setInitialized(true)
            }}
          >
            Begin
          </button>
          {/* </div> */}
        </div>
      </div>
    </>
  );
  let newQuiz: ReactElement = (
    <>

      <button
        type="button"
        className={`${filterState === "A" ? 'bg-indigo-600' : ' bg-gray-400'} inline-flex items-center gap-x-1.5 rounded-md  my-3 px-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
        onClick={(e) => {
          if (e.target) {
            setQuiz();
            setFilterState("B");
            updateFilter("B");
            init();

            if (!playing) {
              playSound();
              setPlaying(true);
            }
            setQuizState(checkQuiz());
          }
          setFrequency(master.filterFrequency);
          setGain(master.filterGain);
        }}
      >
        <RefreshIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
        New
      </button><br />
      <div className="text-sm m-4 font-light leading-6 text-gray-700">
        Sets a new random filter
      </div>
    </>
  );

  let gainSlider: ReactElement = (
    <div className="flex items-center">
      <Slider
        track={false}
        variant="soft"
        aria-label="Gain"
        color={filterState === "B" ? "primary" : "neutral"}
        defaultValue={gain}
        value={gain}
        orientation="vertical"
        size="sm"
        sx={{
          height: 60,
          "--Slider-trackSize": "8px",
          "--Slider-thumbSize": "16px",
          "--Slider-thumbWidth": "16px",
          "--Slider-markSize": "4px",
        }}
        step={12}
        marks={[boostMarks, cutMarks, boostCutMarks][master.level - 1]}
        min={[0, -24, -24][master.level - 1]}
        max={[24, 0, 24][master.level - 1]}
        onChange={(e) => {
          init();
          if (e.target) {
            const target = e.target as HTMLInputElement;
            if (typeof target.value == "number") {
              master.filterGain = target.value;
              setGain(target.value);
              updateFilter("B");
              setFilterState("B");
              setQuizState(checkQuiz());
            }
          }
        }}
      />
    </div>
  );

  let frequencySlider: ReactElement = (
    <Slider
      track={false}
      variant="soft"
      size="sm"
      color={filterState === "B" ? "primary" : "neutral"}
      sx={{
        // height: 80,
        "--Slider-trackSize": "8px",
        "--Slider-thumbSize": "16px",
        "--Slider-thumbWidth": "16px",
        "--Slider-markSize": "4px",
        // padding: 10,
      }}
      aria-label="Frequency"
      defaultValue={exponentialToLinear(frequency, [frequencyMin, frequencyMax])}
      value={exponentialToLinear(frequency, [frequencyMin, frequencyMax])}
      // valueLabelDisplay="auto"
      step={null}
      marks={frequencyMarks}
      min={frequencyMin}
      max={frequencyMax}
      scale={linearToExponentialScale}
      onChange={(e) => {
        init();
        if (e.target) {
          const target = e.target as HTMLInputElement;
          if (typeof target.value == "number") {
            master.filterFrequency = linearToExponential(target.value, [frequencyMin, frequencyMax]);
            setFrequency(master.filterFrequency);
            updateFilter("B");
            setFilterState("B");
            setQuizState(checkQuiz());
          }
        }
      }}
    />
  );
  let volumeSlider: ReactElement = (
    <input
      type="range"
      id="volume-range"
      value={masterVolume}
      min={0}
      max={1}
      step={0.01}
      className="w-full h-2 accent-indigo-600 bg-white rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
      onChange={(e) => {
        init();
        if (e.target) {
          const target = e.target as HTMLInputElement;
          if (typeof target.valueAsNumber == "number") {
            master.masterVolumeAmount = target.valueAsNumber;
            setMasterVolume(master.masterVolumeAmount);
            updateMasterVolume();
          }
        }
      }}
    />
  );

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    master.userAudioURL = URL.createObjectURL(file);

    const { name } = file;
    setFilename(name.replace(/\.[^/.]+$/, ""));
    setSelectedSample(master.audioSamples.length + 1);
    setSelectedSampleName(name.replace(/\.[^/.]+$/, ""));
    master.selectedSample = master.audioSamples.length + 1;
    if (!master.initialized) {
      init();
    } else {
      loadUserAudio();
    }
  };

  let uploadButton: ReactElement = (
    <>
      <div className="flex items-center justify-center w-full" >
        <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
          <div className="flex flex-col items-center justify-center py-4">
            <svg className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
            </svg>
            <p className="mb-2 px-4 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span></p>
            {/* <p className="text-xs text-gray-500 dark:text-gray-400">MP3, WAV, or AIFF</p> */}
          </div>
          <input id="dropzone-file" type="file" className="hidden" onChange={handleFileUpload} />
        </label>
      </div >
    </>
  );

  return (
    <>

      {/* player */}
      {playerRect && (
        <div className={` z-10 w-8 h-6 bg-indigo-400 absolute rounded-2xl`}
          style={{
            left: playerRect.x - 16,
            top: playerRect.y + playerRect.height / 2 - 12
          }}
        >
        </div>
      )}
      {/* filter A */}
      {playerRect && visRect && filterARect && filterState === "A" && (
        <>
          <div className={` z-10 w-8 h-6 bg-indigo-400  absolute rounded-2xl`}
            style={{
              left: filterARect.x - 16,
              top: filterARect.y + filterARect.height / 2 - 12
            }}
          >
          </div>
          <div className={` z-10 w-8 h-6 bg-indigo-400  absolute rounded-2xl`}
            style={{
              left: filterARect.x + filterARect.width - 16,
              top: filterARect.y + filterARect.height / 2 - 12
            }}
          >
          </div>
          <div
            className={`  ${playing ? " movingDottedLine " : " dottedLine "} rounded-xl absolute`}
            style={{
              height: filterARect.y - playerRect.y + (filterARect.height / 2 - playerRect.height / 2) + 6,
              left: playerRect.x - 16,
              top: playerRect.y + playerRect.height / 2 - 6
            }}
          >
          </div>
          <div
            className={`  ${playing ? " movingDottedLine " : " dottedLine "} absolute`}
            style={{
              height: visRect.y - filterARect.y + (visRect.height / 2 - filterARect.height / 2) + 6,
              left: filterARect.x + filterARect.width + 8,
              top: filterARect.y + filterARect.height / 2 - 6
            }}
          >
          </div>
        </>
      )}
      {/* filter B */}
      {playerRect && visRect && filterBRect && filterState === "B" && (
        <>
          <div className={` z-10 w-8 h-6 bg-indigo-400 absolute rounded-2xl`}
            style={{
              left: filterBRect.x - 16,
              top: filterBRect.y + filterBRect.height / 2 - 12
            }}
          >
          </div>
          <div className={` z-10 w-8 h-6 bg-indigo-400 absolute rounded-2xl`}
            style={{
              left: filterBRect.x + filterBRect.width - 16,
              top: filterBRect.y + filterBRect.height / 2 - 12
            }}
          >
          </div>
          <div
            className={`  ${playing ? " movingDottedLine " : " dottedLine "} absolute`}
            style={{
              height: filterBRect.y - playerRect.y + (filterBRect.height / 2 - playerRect.height / 2) + 6,
              left: playerRect.x - 16,
              top: playerRect.y + playerRect.height / 2 - 6
            }}
          >
          </div>
          <div
            className={`  ${playing ? " movingDottedLine " : " dottedLine "} absolute`}
            style={{
              height: visRect.y - filterBRect.y + (visRect.height / 2 - filterBRect.height / 2) + 6,
              left: filterBRect.x + filterBRect.width + 8,
              top: filterBRect.y + filterBRect.height / 2 - 6
            }}
          >
          </div>
        </>
      )}
      {/* visualizer */}
      {visRect && (
        <div className={` z-10 w-8 h-6 bg-indigo-400 absolute rounded-2xl`}
          style={{
            left: visRect.x + visRect.width - 16,
            top: visRect.y + visRect.height / 2 - 12
          }}
        >
        </div>
      )}
      {!initialized &&
        <>
          {initializeWindow}
          <div className="w-full z-30 h-full absolute backdrop-blur-sm">
          </div>
        </>
      }

      <div
        className={`${!initialized ?? 'backdrop-blur-lg opacity-50'} max-w-3xl m-auto h-full p-8`}
      >

        <div className="grid grid-cols-1 gap-2 min-[400px]:grid-cols-2">
          <div><FilterSelect master={master} setLevel={setLevel} /></div>
          <div><SampleSelect
            master={master}
            setSelectedSample={setSelectedSample}
            setSelectedSampleName={setSelectedSampleName}
            selectedSampleName={selectedSampleName}
            filename={filename}
          /></div>
          <div className="min-[400px]:order-1">
            {uploadButton}
          </div>
          <div ref={playerRef} className="h-24 z-20 bg-indigo-50 flex shadow-indigo-900 shadow-inner rounded-md">
            <div className="m-4">
              {playButton}
            </div>
            <div className="flex w-full items-center mr-2 gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4F46E5" className="w-6 h-6">
                <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z" />
              </svg>

              {volumeSlider}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4F46E5" className="w-6 h-6">
                <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 11-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" />
                <path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="text-sm my-5 w-full font-medium text-center text-gray-500">
          Click below to switch the filters
        </div>

        <div
          ref={filterARef}
          className={` relative z-20 cursor-pointer mb-3 w-full h-16 align-middle ${filterState === "B" ? "  shadow shadow-gray-500 bg-gray-50 hover:bg-white" : " shadow-indigo-900 shadow-inner bg-indigo-50 "} " items-center justify-center flex-col  rounded`}
          onClick={() => {
            updateFilter("A");
            setFilterState("A");
          }}
        >
          <div className="flex flex-row">

            <span
              id="A"
              className={`${filterState === "A" ? 'text-indigo-600' : ' text-gray-400'} text-2xl p-2 w-20 font-medium text-center justify-center leading-6`}
            >
              Filter<br />A
            </span>

            {newQuiz}
          </div>
        </div>
        {/* text-white bg-green-400 border-white */}
        {/* border-green-600 bg-green-50 text-green-600 */}
        <div className={`${quizState ? '  bg-green-100 text-green-700' : 'text-gray-500'} text-sm rounded-lg p-2 w-fit m-auto font-medium my-5 text-center`}>
          {quizState ? <span>Correct! Click <strong>New</strong> to try another one</span> : <span>Move the sliders in <strong>Filter B</strong>	↓ to match the sound of <strong>Filter A</strong> ↑</span>}
        </div>
        <div ref={filterBRef} className=" relative z-20 cursor-pointer my-3 w-full">
          <div
            className={` ${filterState !== "B" ? " shadow shadow-gray-500 bg-gray-50 hover:bg-white" : " shadow-indigo-900 shadow-inner bg-indigo-50 "} " items-center justify-center flex-col rounded`}
            onClick={() => {
              updateFilter("B");
              setFilterState("B");
            }}
          >
            <div className="flex flex-row">
              <span
                id="B"
                className={`${filterState === "B" ? 'text-indigo-600' : ' text-gray-400'} text-2xl p-2 w-20 m-auto font-medium text-center justify-center leading-6`}
              >
                Filter<br />B
              </span>

              <div className="w-24 mt-5 mb-2">
                {gainSlider}
                <span className="text-xs text-center text-gray-500"
                  id="gainText">
                  gain (dB)
                </span>
              </div>
              <div className="w-full m-auto pr-6">
                {frequencySlider}
                <p
                  className="text-xs text-gray-500 text-center p-1"
                  id="frequencyText">
                  frequency (Hz)
                </p>
              </div>
            </div>
          </div>
        </div >
        <div ref={visRef} className="relative z-20 shadow-indigo-900 shadow-inner bg-indigo-50 rounded-md">
          <div className='absolute'
            style={{
              left: "calc(50% - 42px)"
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4F46E5" className="w-24 h-24 scale-x-[-1] opacity-20">
              <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 11-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" />
              <path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" />
            </svg>
          </div>
          <canvas ref={canvasRef} width={702} height={100} />
        </div>
      </div >

    </>
  );
};
